"use client";

import { usePathname, useRouter } from "next/navigation";

import { Button } from "@/components/ui";
import AIBadge from "@/components/ui/ai-badge";

const RESERVED_FIRST_SEGMENTS = new Set([
  "login",
  "onboarding",
  "clinic-setup",
]);

function resolveHomeFromPath(pathname: string | null): string {
  if (!pathname) return "/login";
  const segments = pathname.split("/").filter(Boolean);
  const firstSegment = segments[0];
  if (!firstSegment || RESERVED_FIRST_SEGMENTS.has(firstSegment)) {
    return "/login";
  }
  // Keep patient-portal users on the portal — sending them to /dashboard
  // would land on a provider page they can't access.
  if (segments.includes("patient-portal")) {
    return `/${firstSegment}/patient-portal`;
  }
  return `/${firstSegment}/dashboard`;
}

export default function NotFound() {
  const router = useRouter();
  const pathname = usePathname();

  // Route to the tenant home (or /login if no tenant context in the URL) to
  // avoid bouncing back into /login via router.back() when this page was
  // rendered from a post-login returnTo redirect to a non-existent route.
  const goHome = () => {
    router.push(resolveHomeFromPath(pathname));
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background">
      <div className="max-w-md rounded-lg border border-border p-6">
        <h2 className="mb-4 flex items-center gap-2 text-h1 font-medium">
          <AIBadge size={24} />
          This page cannot be found
        </h2>
        <p className="mb-6">
          This page does not exist. Please check the URL and try again.
        </p>
        <Button variant="secondary" onClick={goHome}>
          Go to Home
        </Button>
      </div>
    </div>
  );
}
